import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  uomLadderService,
  type UomLadderCreateRequest,
  type UomLadderListFilters,
  type UomLadderResolveParams,
  type UomLadderSupersedeRequest,
} from '../../services/api/uomLadderService';

/**
 * Cache key used by `uomLadderSlice.resolved` so duplicate panels for the
 * same item under the same buyer/supplier context share state. Keep in
 * sync with `useUomLadder` (Phase I).
 */
export function resolveCacheKey(p: UomLadderResolveParams): string {
  return [
    p.tenantId,
    p.itemId,
    p.buyerPartyId ?? '_',
    p.supplierPartyId ?? '_',
    p.channelCode ?? '_',
    p.asOf ?? '_',
  ].join('|');
}

export const fetchUomLadderList = createAsyncThunk(
  'uomLadder/fetchList',
  async (filters: UomLadderListFilters, { rejectWithValue }) => {
    try {
      return await uomLadderService.list(filters);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch UoM ladder rows');
    }
  }
);

export const resolveUomLadder = createAsyncThunk(
  'uomLadder/resolve',
  async (params: UomLadderResolveParams, { rejectWithValue }) => {
    try {
      const data = await uomLadderService.resolve(params);
      return { key: resolveCacheKey(params), data };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to resolve UoM ladder');
    }
  }
);

export const createUomLadder = createAsyncThunk(
  'uomLadder/create',
  async (
    { tenantId, request }: { tenantId: number; request: UomLadderCreateRequest },
    { rejectWithValue }
  ) => {
    try {
      return await uomLadderService.create(tenantId, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create UoM ladder');
    }
  }
);

export const supersedeUomLadder = createAsyncThunk(
  'uomLadder/supersede',
  async (
    { tenantId, id, request }: { tenantId: number; id: number; request: UomLadderSupersedeRequest },
    { rejectWithValue }
  ) => {
    try {
      return await uomLadderService.supersede(tenantId, id, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to supersede UoM ladder');
    }
  }
);

export const deleteUomLadder = createAsyncThunk(
  'uomLadder/delete',
  async (
    { tenantId, id }: { tenantId: number; id: number },
    { rejectWithValue }
  ) => {
    try {
      await uomLadderService.remove(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete UoM ladder');
    }
  }
);
